import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { TranslateService } from '@ngx-translate/core';
import { FcuUnitSocketService } from '@service/fcu-unit-socket.service';
import { FcuUnitServiece } from '@service/fcu-unit.service';
import { FuUnitService } from '@service/fu-unit.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { FcuUnit, FcuUnitFull, fcuUnitMax } from 'icreate/common/models/fcu-unit';
import { Vessel } from 'icreate/common/models/vessel';
import { Subject, takeUntil, lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-system-setting-fcu',
    templateUrl: './system-setting-fcu.component.html',
    styleUrls: ['./system-setting-fcu.component.css'],
    standalone: false,
})
export class SystemSettingFcuComponent implements OnInit, OnDestroy {
    readonly FCU_UNIT_MAX = fcuUnitMax;

    @Input() vessel: Vessel;
    @Input() canEditSettings: boolean;
    @ViewChild('deleteDialog') deleteDialog: ConfirmationWindowComponent;

    fcuUnitList: (FcuUnit & { fuNum?: number })[] = [];
    selectedFcuUnit: FcuUnit;
    isOpenOption: boolean = false;
    deleteDialogMainLabel: string = '';
    isLoading: boolean = true;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private router: Router,
        private fcuUnitService: FcuUnitServiece,
        private fcuUnitSocketService: FcuUnitSocketService,
        private fuUnitService: FuUnitService,
        private translateService: TranslateService,
        private messageDialogService: MessageDialogService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        this.isOpenOption = false;
    }

    async ngOnInit() {
        this.fcuUnitList = await this.fcuUnitService.getList(this.vessel.id);

        for (let i = 0; i < this.fcuUnitList.length; i++) {
            const fuUnitList = await this.fuUnitService.getList(this.fcuUnitList[i].id);
            this.fcuUnitList[i].fuNum = fuUnitList.length;
        }

        this.fcuUnitSocketService
            .fcuUnitDuplicate$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async ({ newFcuUnit, prevFcuUnitId }) => {
                if (newFcuUnit.vesselId !== this.vessel.id) return;
                try {
                    const fuUnitList = await this.fuUnitService.getList(newFcuUnit.id);
                    const item = { ...newFcuUnit, fuNum: fuUnitList.length };
                    if (this.fcuUnitList.some(u => u.id === item.id)) return;
                    this.fcuUnitList = [...this.fcuUnitList, item];
                    const prevFcu = this.fcuUnitList.find(opsUnit => opsUnit.id === prevFcuUnitId);
                    this.showMessageDialog('system-settings.fcu.message-dialog.duplicate.message', {
                        prevIndex: prevFcu.index,
                        copiedIndex: newFcuUnit.index,
                    });
                } catch (e) {
                    console.error(e);
                }
            });

        this.fcuUnitSocketService
            .fcuUnitUpdated$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(async (updatedFcuUnitFull: FcuUnitFull) => {
                if (updatedFcuUnitFull.fcuUnit.vesselId !== this.vessel.id || !updatedFcuUnitFull?.fuUnitList) return;
                const fuNum = updatedFcuUnitFull.fuUnitList.length;
                const { network, ...fcuWithoutNetwork } = updatedFcuUnitFull.fcuUnit;
                this.fcuUnitList = this.fcuUnitList.map(fcuUnit =>
                    fcuUnit.id === updatedFcuUnitFull.fcuUnit.id
                        ? { ...(fcuWithoutNetwork as FcuUnit), fuNum }
                        : fcuUnit
                );

                this.showMessageDialog('system-settings.fcu.message-dialog.update.message', {
                    index: updatedFcuUnitFull.fcuUnit.index,
                });
            });

        this.fcuUnitSocketService
            .fcuUnitDeleted$()
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(({ fcuUnitId }) => {
                let indexToDelete = this.fcuUnitList.findIndex(fcuUnit => fcuUnit.id === fcuUnitId);
                if (indexToDelete !== -1) {
                    this.showMessageDialog('system-settings.fcu.message-dialog.delete.message', {
                        index: this.fcuUnitList[indexToDelete].index,
                    });
                    this.fcuUnitList.splice(indexToDelete, 1);
                    this.fcuUnitList = this.fcuUnitList.map((opsUnit, index) => ({
                        ...opsUnit,
                        index: index + 1,
                    }));
                }
            });

        this.isLoading = false;
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    openNewFcuSetting() {
        if (!this.canEditSettings) return;
        this.router.navigateByUrl(`${this.vessel.id}/fcu`);
    }

    openMoreOption(fcuUnit: FcuUnit) {
        if (this.isOpenOption) {
            this.isOpenOption = false;
        } else {
            this.isOpenOption = true;
            this.selectedFcuUnit = fcuUnit;
        }
    }

    goViewFcuSetting(fcuUnitId: string) {
        this.router.navigateByUrl(`${this.vessel.id}/fcu/${fcuUnitId}/view`, {
            state: {
                isEditable: this.canEditSettings,
            },
        });
    }

    editFcuUnit(fcuUnitId: string) {
        if (!this.canEditSettings) return;
        this.router.navigateByUrl(`${this.vessel.id}/fcu/${fcuUnitId}`);
    }

    async duplicateFcuUnit(fcuUnitId: string) {
        if (!this.canEditSettings || this.fcuUnitList?.length >= this.FCU_UNIT_MAX) return;
        try {
            this.isLoading = true;
            await this.fcuUnitService.duplicateFcuUnit(fcuUnitId);
            this.isOpenOption = false;
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    async deleteFcuUnit(fcuUnitId: string) {
        if (!this.canEditSettings || this.fcuUnitList?.length <= 1) return;
        try {
            this.deleteDialogMainLabel = this.translateService.instant('system-settings.fcu.delete.main.title', {
                index: this.selectedFcuUnit.index,
            });

            this.deleteDialog.setVisible(true);
            const option = await lastValueFrom(this.deleteDialog.onOptionSelected.pipe(take(1)));

            if (option === ConfirmationWindowTypes.SAVE) {
                this.isLoading = true;
                await this.fcuUnitService.deleteFcuUnit(fcuUnitId);
                this.selectedFcuUnit = null;
                this.isOpenOption = false;
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.isLoading = false;
        }
    }

    async showMessageDialog(message: string, params?: Record<string, any>) {
        this.messageDialogService.openMessage(message, params);
    }
}
